import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { clearStaffSessionStorage } from '../utils/staffSessionGuard';
import './Navbar.css';
import logoDark from '../Logos/LogoElefanteBordesBlancos.png';
import logoLight from '../Logos/AlegraLogoNegro.png';
import { useEditMode } from '../contexts/EditModeContext';
import useGlobalTheme from '../hooks/useGlobalTheme';
import { canAccessAdminShell } from '../config/staffFeatures';

const Navbar = ({ cart }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const { isEditMode, toggleEditMode, isAdmin, userRole, isAuthenticated, allowedFeatures } = useEditMode();
  const { isDarkMode } = useGlobalTheme();
  const canOpenAdminPanel = canAccessAdminShell(isAdmin, userRole, allowedFeatures);

  const logoClickCountRef = useRef(0);
  const lastLogoClickRef = useRef(0);
  const logoClickTimeoutRef = useRef(null);

  const SECRET_CLICKS_REQUIRED = 3;
  const SECRET_CLICK_WINDOW_MS = 1500;

  const formatNumber = (number) => {
    if (typeof number !== 'number' || isNaN(number)) {
      return '0,00';
    }
    return number
      .toFixed(2)
      .replace('.', ',')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  useEffect(() => {
    const logoImage = document.querySelector('.logo img');
    if (logoImage) {
      logoImage.classList.add('loaded');
    }

    const handleClickOutside = (event) => {
      if (
        isOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !hamburgerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const calculateCartTotals = () => {
      const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const quantity = cart.reduce((sum, item) => sum + item.quantity, 0);

      let discountAmount = 0;
      cart.forEach((item) => {
        if (item.discount) {
          discountAmount += (item.price * item.discount) / 100 * item.quantity;
        }
      });

      setTotalPrice(subtotal - discountAmount);
      setTotalQuantity(quantity);
    };

    calculateCartTotals();
  }, [cart]);

  const handleLogoClick = (e) => {
    e.preventDefault();
    const now = Date.now();

    if (now - lastLogoClickRef.current > SECRET_CLICK_WINDOW_MS) {
      logoClickCountRef.current = 1;
    } else {
      logoClickCountRef.current += 1;
    }
    lastLogoClickRef.current = now;

    if (logoClickTimeoutRef.current) {
      clearTimeout(logoClickTimeoutRef.current);
      logoClickTimeoutRef.current = null;
    }

    if (logoClickCountRef.current >= SECRET_CLICKS_REQUIRED) {
      logoClickCountRef.current = 0;
      lastLogoClickRef.current = 0;
      navigate('/login');
      return;
    }

    logoClickTimeoutRef.current = setTimeout(() => {
      if (logoClickCountRef.current === 1) {
        navigate('/');
      }
      logoClickCountRef.current = 0;
      lastLogoClickRef.current = 0;
      logoClickTimeoutRef.current = null;
    }, SECRET_CLICK_WINDOW_MS);
  };

  useEffect(() => {
    return () => {
      if (logoClickTimeoutRef.current) {
        clearTimeout(logoClickTimeoutRef.current);
      }
    };
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async () => {
    clearStaffSessionStorage();
    try {
      await signOut(auth);
    } catch (_) {
      /* ignore */
    }
    navigate('/login');
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <button
          type="button"
          className="logo"
          onClick={handleLogoClick}
          aria-label="Ir al inicio"
        >
          <img
            src={isDarkMode ? logoDark : logoLight}
            alt="Allegra Club House"
            draggable="false"
          />
        </button>
        <ul className={`nav-links ${isOpen ? 'open' : ''}`} ref={menuRef}>
          <li>
            <Link to="/entradas" onClick={toggleMenu}>
              Entradas
            </Link>
          </li>
          <li>
            <Link to="/consumo-interno" onClick={toggleMenu}>
              Consumo interno
            </Link>
          </li>
          <li>
            <Link to="/nosotros" onClick={toggleMenu}>
              Nosotros
            </Link>
          </li>
          <li>
            <Link to="/contacto" onClick={toggleMenu}>
              Contacto
            </Link>
          </li>
          <li>
            <Link to="/carrito" className="nav-cart-link" onClick={toggleMenu}>
              🛒{' '}
              {totalQuantity > 0 && (
                <span className="cart-info">
                  ({totalQuantity}) ${formatNumber(totalPrice)}
                </span>
              )}
            </Link>
          </li>
          {canOpenAdminPanel && (
            <li>
              <Link to="/admin" onClick={toggleMenu}>
                Admin
              </Link>
            </li>
          )}
          {isAdmin && (
            <li>
              <button
                type="button"
                onClick={toggleEditMode}
                className={`edit-mode-btn ${isEditMode ? 'active' : ''}`}
                title={isEditMode ? 'Desactivar modo edición' : 'Activar modo edición'}
                aria-label={isEditMode ? 'Desactivar modo edición' : 'Activar modo edición'}
                aria-pressed={isEditMode}
              >
                {isEditMode ? '✏️' : '📝'}
              </button>
            </li>
          )}
          {isAuthenticated && (
            <li>
              <button type="button" className="nav-logout-btn" onClick={handleLogout} aria-label="Cerrar sesión">
                Salir
              </button>
            </li>
          )}
        </ul>
        <div
          className={`hamburger ${isOpen ? 'open' : ''}`}
          onClick={toggleMenu}
          ref={hamburgerRef}
          role="button"
          aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={isOpen}
        >
          <span className="line"></span>
          <span className="line"></span>
          <span className="line"></span>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
